import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../api/axiosInstance';

// Get user by ID thunk
export const getUserById = createAsyncThunk(
  'auth/getUserById',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/users/${userId}`);
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue('Failed to fetch user');
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch user'
      );
    }
  }
);

// Device list thunk
export const fetchDevices = createAsyncThunk(
  'api/fetchDevices',
  async (
    { page = 1, limit = 10, searchBy = '', sortBy = '', order = 'ASC' } = {},
    { rejectWithValue }
  ) => {
    try {
      let url = `/api/devices?page=${page}&limit=${limit}`;
      if (searchBy && searchBy.trim() !== '') {
        url += `&searchBy=${encodeURIComponent(searchBy)}`;
      }
      if (sortBy && sortBy.trim() !== '') {
        url += `&sortBy=${encodeURIComponent(sortBy)}`;
      }
      if (order && order.trim() !== '') {
        url += `&order=${order}`;
      }
      const response = await axiosInstance.get(url);
      if (response.status === 200) {
        return response.data || [];
      } else {
        return rejectWithValue('Failed to fetch devices');
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch devices'
      );
    }
  }
);
// Reset password thunk
export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/api/forgot-password/update-password',
        data
      );
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue('Reset password failed');
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        return rejectWithValue(error.response.data.error);
      }
      return rejectWithValue(error.message || 'Reset password failed');
    }
  }
);

// Verify code thunk
export const verifyCode = createAsyncThunk(
  'auth/verifyCode',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/api/forgot-password/verify',
        data
      );
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue('Verification failed');
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        return rejectWithValue(error.response.data.error);
      }
      return rejectWithValue(error.message || 'Verification failed');
    }
  }
);

// Resend code thunk
export const resendCode = createAsyncThunk(
  'auth/resendCode',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/api/forgot-password/resend',
        data
      );
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue('Resend code failed');
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        return rejectWithValue(error.response.data.error);
      }
      return rejectWithValue(error.message || 'Resend code failed');
    }
  }
);

// ...existing code...

export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/api/forgot-password/request',
        data
      );
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue('Failed to send reset instructions');
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        return rejectWithValue(error.response.data.error);
      }
      return rejectWithValue(
        error.message || 'Failed to send reset instructions'
      );
    }
  }
);

export const login = createAsyncThunk(
  'auth/login',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/api/auth/login', data);
      if (response.status === 200) {
        console.log('response.data', response.data);
        return response.data;
      } else {
        return rejectWithValue('Login failed');
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        return rejectWithValue(error.response.data.error);
      }
      return rejectWithValue(error.message || 'Login failed');
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/api/users', data);
      if (response.status === 201) {
        return response.data;
      } else {
        return rejectWithValue('Registration failed');
      }
    } catch (error) {
      if (error.response && error.response.status === 400) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(
        error.response?.data?.message || 'Registration failed'
      );
    }
  }
);

// Device analysis thunk
export const fetchDeviceAnalysis = createAsyncThunk(
  'api/fetchDeviceAnalysis',
  async ({ deviceId, userId }, { rejectWithValue }) => {
    try {
      let url = `api/device-readings/by-device-and-user?deviceId=${deviceId}`;
      if (userId) {
        url += `&userId=${userId}`;
      }
      const response = await axiosInstance.get(url);
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue('Failed to fetch device analysis');
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch device analysis'
      );
    }
  }
);

// Chart data thunk
export const fetchDeviceChartData = createAsyncThunk(
  'api/fetchDeviceChartData',
  async (payload, { rejectWithValue }) => {
    try {
      // Pass deviceId and date as query params
      const response = await axiosInstance.post(
        `/api/device-readings/stats`,
        payload
      );
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue('Failed to fetch chart data');
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch chart data'
      );
    }
  }
);

export const fetchSummaryCounts = createAsyncThunk(
  'api/fetchSummaryCounts',
  async (_, { rejectWithValue }) => {
    try {
      // Adjust endpoint as needed
      const response = await axiosInstance.get('/api/kpi');
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue('Failed to fetch summary counts');
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch summary counts'
      );
    }
  }
);

// Set technician password thunk
export const setTechnicianPassword = createAsyncThunk(
  'auth/setTechnicianPassword',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/api/technicians/set-password',
        { email, password }
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || 'Failed to set technician password'
      );
    }
  }
);

// Logout thunk
export const logoutApi = createAsyncThunk(
  'auth/logoutApi',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/api/auth/logout');
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue('Logout failed');
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Logout failed');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    token: null,
    loading: false,
    error: null,
    devices: [],
    chartData: null,
    chartDataLoading: false,
    chartDataError: null,
    deviceName: null,
    deviceId: null,
    summaryCounts: {
      totalUsers: 0,
      totalTechnicians: 0,
      devicesOnline: 0,
      devicesOffline: 0,
    },
    summaryCountsLoading: false,
    summaryCountsError: null,
    dashboardSearch: '',
    dashboardPage: 1,
    dashboardSortBy: '',
    dashboardOrder: 'ASC',
  },
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.error = null;
      localStorage.removeItem('jwt');
    },
    setDashboardSearch: (state, action) => {
      state.dashboardSearch = action.payload;
    },
    setDashboardPage: (state, action) => {
      state.dashboardPage = action.payload;
    },
    setDashboardSort: (state, action) => {
      state.dashboardSortBy = action.payload.sortBy;
      state.dashboardOrder = action.payload.order;
    },
    setDeviceName: (state, action) => {
      state.deviceName = action.payload;
    },
    setDeviceId: (state, action) => {
      state.deviceId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Summary counts
      .addCase(fetchSummaryCounts.pending, (state) => {
        state.summaryCountsLoading = true;
        state.summaryCountsError = null;
      })
      .addCase(fetchSummaryCounts.fulfilled, (state, action) => {
        state.summaryCountsLoading = false;
        state.summaryCounts = action.payload;
      })
      .addCase(fetchSummaryCounts.rejected, (state, action) => {
        state.summaryCountsLoading = false;
        state.summaryCountsError = action.payload;
      })
      .addCase(login.pending, (state) => {
        state.loading = true;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        // If token is present in response, save it; otherwise, just set user
        if (action.payload.token) {
          state.token = action.payload.token;
          localStorage.setItem('jwt', action.payload.token);
        } else {
          state.token = 'dummy-token'; // Set a dummy token to trigger redirect
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(register.pending, (state) => {
        state.loading = true;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        localStorage.setItem('jwt', action.payload.token);
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Device list
      .addCase(fetchDevices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDevices.fulfilled, (state, action) => {
        state.loading = false;
        state.devices = action.payload;
      })
      .addCase(fetchDevices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Chart data
      .addCase(fetchDeviceChartData.pending, (state) => {
        state.chartDataLoading = true;
        state.chartDataError = null;
      })
      .addCase(fetchDeviceChartData.fulfilled, (state, action) => {
        state.chartDataLoading = false;
        state.chartData = action.payload;
      })
      .addCase(fetchDeviceChartData.rejected, (state, action) => {
        state.chartDataLoading = false;
        state.chartDataError = action.payload;
      })
      // Device analysis
      .addCase(fetchDeviceAnalysis.pending, (state) => {
        state.chartDataLoading = true;
        state.chartDataError = null;
      })
      .addCase(fetchDeviceAnalysis.fulfilled, (state, action) => {
        state.chartDataLoading = false;
        state.chartData = action.payload.data;
        state.deviceName = action.payload.device.name;
      })
      .addCase(fetchDeviceAnalysis.rejected, (state, action) => {
        state.chartDataLoading = false;
        state.chartDataError = action.payload;
      })
      .addCase(logoutApi.fulfilled, (state, action) => {
        state.user = null;
        state.token = null;
        state.error = null;
        state.dashboardSearch = null;
      })
      .addCase(logoutApi.rejected, (state, action) => {
        state.error = action.payload || 'Logout failed';
      });
  },
});

export const {
  logout,
  setDeviceName,
  setDeviceId,
  setDashboardSearch,
  setDashboardPage,
  setDashboardSort,
} = authSlice.actions;
export default authSlice.reducer;
